/**
 * Build & submit, for an ACS Ads Manager ticket.
 *
 * Same loop as the Audience+ dialog — Build, look, Submit — under ACS's vendor
 * guide, which changes three things:
 *
 *   Names     are not ours. The ticket description lists each asset's file
 *             name; two parts we fill in by rule: `<vendor_name>` (always
 *             acsplayin) and `<ai_usage>` (always aiassisted). Any OTHER
 *             `<tag>` (`<concept1>`, `<template_name>`, … — the customer may
 *             add or rename them without notice) is explained in prose under
 *             the name, so the container's model suggests a value for it; the
 *             person sees it, can type over it, and has to confirm what the
 *             model filled before a build. Each file here is matched to one
 *             listed name, and the build stages it under exactly that name —
 *             so what is previewed is what ships.
 *   Drive     is ACS's folder, linked from the ticket a few minutes after it is
 *             claimed. Until it appears there is nothing to submit to.
 *   Asana     Submit writes the preview link into the open subtask's Preview
 *             Link field AND posts the same link as a comment on that subtask
 *             (ACS requires both since 2026-10-16), assigns that subtask and
 *             the main task to the ACS Owner, and moves the main task to
 *             Pending for Approval. Nothing here creates a subtask, comments
 *             on the main task, or completes anything.
 *
 * Claiming (assign + vendor field + IN PRODUCTION) is done by hand for now, in
 * Asana, before the agent is started.
 *
 * Build and Submit are editor jobs (useSnapshotJobs), exactly as in the
 * Audience+ dialog: this can be closed the moment one starts.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import BuildWaitLine from "../buildWaitLine";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LaunchIcon from "@material-ui/icons/Launch";
import RefreshIcon from "@material-ui/icons/Refresh";
import {
  deliverTask,
  describeJobFailureWithReasons,
  jobFailureCode,
  asanaAdsManagerFillTags,
  previewTask,
} from "../../modules/snapshotEditorApi";
import { collectFolderPaths, normalizeFolderPath } from "../../modules/snapshotTree";
import { BAR_BG, CONTROL_BG, CONTROL_BORDER, FIELD_LABEL, PANEL_BG } from "./panelPalette";
import { formatBytes, formatStamp } from "./taskDeliveryFormat";
import TaskReviewButton from "./taskReviewButton";

/** Our tag in the `<vendor_name>` slot. Mirrors VENDOR_TAG in the container. */
export const VENDOR_TAG = "acsplayin";

/**
 * What we put in the `<ai_usage>` slot. The guide allows ai, aiassisted or
 * human; our creatives are always aiassisted. Mirrors AI_USAGE in the container.
 */
export const AI_USAGE = "aiassisted";

const STEP_LABELS = {
  plec: "AppLovin preview tool",
  drive: "Google Drive",
  asana: "Asana updates",
};

const HANDOFF_LABELS = {
  preview_link: "Preview Link field on",
  preview_comment: "Comment the link on",
  assign_subtask: "Assign subtask",
  assign_task: "Assign main task",
  move_task: "Move main task",
};

/** The name's tokens, in order, as the build's per-file overrides call them. */
const TOKEN_KEYS = [
  "brand", "vendor", "creativeType", "date", "seq", "assignee",
  "concept1", "category", "concept3", "subconcept", "theme",
];

const BORDER = "1px solid rgba(255,255,255,0.09)";

const useStyles = makeStyles({
  section: { marginTop: "20px" },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "8px",
  },
  sectionTitle: {
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: FIELD_LABEL,
  },
  card: { border: BORDER, borderRadius: "6px", padding: "10px 12px" },
  cardRaised: { backgroundColor: BAR_BG, border: BORDER, borderRadius: "8px", padding: "12px 14px" },
  summaryName: { fontSize: "15px", fontWeight: 500, wordBreak: "break-word", lineHeight: 1.4 },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" },
  chip: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "10px",
    border: BORDER,
    backgroundColor: "rgba(0,0,0,0.18)",
    color: FIELD_LABEL,
    whiteSpace: "nowrap",
  },
  chipOk: { color: "#a5d6a7", borderColor: "rgba(165,214,167,0.35)" },
  chipWarn: { color: "#e0b96a", borderColor: "rgba(224,185,106,0.35)" },

  fact: { display: "flex", gap: "10px", fontSize: "13px", padding: "3px 0", alignItems: "baseline" },
  factName: { flex: "0 0 130px", color: FIELD_LABEL, fontSize: "12px" },
  factValue: { flex: 1, minWidth: 0, wordBreak: "break-all" },

  fileEntry: { padding: "8px 6px" },
  fileRowDivided: { borderTop: BORDER },
  fileHead: { display: "flex", gap: "10px", alignItems: "center", fontSize: "12px", color: FIELD_LABEL },
  fileControls: { display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" },
  fileName: {
    flex: 1,
    minWidth: 0,
    marginTop: "6px",
    fontFamily: "monospace",
    fontSize: "12px",
    lineHeight: 1.55,
    wordBreak: "break-all",
  },
  filled: { color: "#4eabb1", fontWeight: 600 },
  missing: { color: "#e0b96a", fontWeight: 600 },
  tagFields: { display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "8px", width: "100%" },
  fileSize: { fontSize: "11px", color: FIELD_LABEL, whiteSpace: "nowrap" },
  fileLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    borderRadius: "3px",
    backgroundColor: CONTROL_BG,
    border: `1px solid ${CONTROL_BORDER}`,
    color: FIELD_LABEL,
    "&:hover": { color: "#4eabb1", borderColor: "#4eabb1" },
  },

  note: { fontSize: "11px", color: FIELD_LABEL, opacity: 0.85, marginTop: "6px", lineHeight: 1.5 },
  warning: { fontSize: "12px", color: "#e0b96a", marginTop: "6px", lineHeight: 1.5 },
  failure: { fontSize: "12px", color: "#ef9a9a", marginTop: "6px", wordBreak: "break-word" },
  ok: { fontSize: "12px", color: "#a5d6a7", marginTop: "6px" },
  busy: { display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" },
  waitLine: { display: "block", fontSize: "12px", color: FIELD_LABEL, marginTop: "2px" },
  step: { display: "flex", gap: "8px", fontSize: "13px", padding: "3px 0" },
  stepName: { flex: "0 0 170px", color: FIELD_LABEL },
  driveTargets: { marginLeft: "16px", marginBottom: "3px" },
  driveTargetLink: { display: "inline-flex", alignItems: "baseline", gap: "4px", minWidth: 0, fontSize: "12px", wordBreak: "break-all" },
  iconButton: { minWidth: 0, padding: "2px 6px" },
});

const TAG_PATTERN = /<[a-z0-9_]+>/gi;

/** The `<tag>`s of a listed name that are not ours, lowercase, once each, in order. */
export function extraTags(template) {
  const tags = [];
  for (const match of String(template || "").matchAll(TAG_PATTERN)) {
    const tag = match[0].slice(1, -1).toLowerCase();
    if (tag !== "vendor_name" && tag !== "ai_usage" && !tags.includes(tag)) tags.push(tag);
  }
  return tags;
}

/** A typed tag value as a name token wants it, or null: one word, lowercase letters and digits. */
export function cleanTagValue(value) {
  const word = String(value == null ? "" : value).trim().toLowerCase();
  return /^[a-z0-9]+$/.test(word) ? word : null;
}

/**
 * A listed name with our two placeholders filled in, and every other tag that
 * `values` holds a valid word for. Mirrors fillDeliveryName in the container,
 * which checks the result against the ticket again. A tag nobody has filled
 * stays as `<tag>`, which can never be built.
 */
export function fillName(template, values = {}) {
  return String(template || "").replace(TAG_PATTERN, (tag) => {
    const key = tag.slice(1, -1).toLowerCase();
    if (key === "vendor_name") return VENDOR_TAG;
    if (key === "ai_usage") return AI_USAGE;
    return cleanTagValue(values[key]) || tag;
  });
}

/**
 * The per-file overrides that make the build stage a file under `name`, or
 * null when the name cannot be one: every token is spelled out, so the
 * snapshot's own name contributes nothing.
 */
export function overridesFor(name) {
  const tokens = String(name || "").split("_");
  if (tokens.length !== TOKEN_KEYS.length) return null;
  // The build keeps letters and digits only (cleanToken); a token with anything
  // else would ship under a different name than the ticket asked for.
  if (tokens.some((token) => !/^[a-z0-9]+$/.test(token))) return null;
  if (!/^\d{8}$/.test(tokens[3]) || !/^\d{2}$/.test(tokens[4])) return null;
  if (tokens[2] !== "sip" && tokens[2] !== "mip") return null;

  const overrides = _.zipObject(TOKEN_KEYS, tokens);
  overrides.seq = Number(overrides.seq);
  return overrides;
}

/** Where a name comes from the ticket and where we filled it in, for display. */
function NameParts({ template, values, classes }) {
  const parts = String(template || "").split(/(<[a-z0-9_]+>)/i);
  return (
    <>
      {parts.map((part, index) => {
        const tag = /^<([a-z0-9_]+)>$/i.exec(part);
        // eslint-disable-next-line react/no-array-index-key
        if (!tag) return <span key={index}>{part}</span>;
        const key = tag[1].toLowerCase();
        const value = key === "vendor_name" ? VENDOR_TAG : key === "ai_usage" ? AI_USAGE : cleanTagValue(values[key]);
        // eslint-disable-next-line react/no-array-index-key
        return <span key={index} className={value ? classes.filled : classes.missing}>{value || part}</span>;
      })}
    </>
  );
}

/** The folder's snapshots, in the order the server lists them. */
function folderSips(snapshots, folder) {
  const target = normalizeFolderPath(folder || "");
  const seqOf = (snapshot) => {
    const tokens = String(snapshot.exportName || "").split("_");
    return /^\d{2}$/.test(tokens[4] || "") ? Number(tokens[4]) : 99;
  };
  return _.sortBy(
    (snapshots || []).filter((snapshot) => normalizeFolderPath(snapshot.exportFolder || "") === target),
    [seqOf, "fileName"]
  );
}

/**
 * Each file, the ticket name it ships under, and what is wrong with it.
 *
 * Files are matched to the listed names in order — one ticket is one asset for
 * now, so this is usually one to one — and the match can be changed per file.
 */
export function deliveryRows(sips, names, choices) {
  const rows = sips.map((sip, index) => {
    const choice = (choices && choices[sip.fileName]) || {};
    const template = choice.template !== undefined ? choice.template : names[index] || "";
    const tags = extraTags(template);
    const values = choice.tags || {};
    // What the model filled and nobody has touched since.
    const aiTags = (choice.ai || []).filter((tag) => tags.includes(tag) && cleanTagValue(values[tag]));
    const name = template ? fillName(template, values) : null;
    const overrides = name ? overridesFor(name) : null;
    const open = tags.filter((tag) => !String(values[tag] || "").trim());
    const invalid = tags.filter((tag) => String(values[tag] || "").trim() && !cleanTagValue(values[tag]));
    let problem = null;
    if (!template) problem = "No name from the ticket is chosen for this file.";
    else if (open.length) problem = `Fill in: ${open.join(", ")}.`;
    else if (invalid.length) {
      problem = `${invalid.join(", ")}: one word of lowercase letters and digits.`;
    } else if (!overrides) problem = "This name cannot be built as written — ask ACS to correct the ticket.";
    return { sip, template, tags, values, aiTags, name, overrides, problem };
  });

  const counts = _.countBy(rows.filter((row) => row.name), "name");
  return rows.map((row) =>
    row.name && counts[row.name] > 1 && !row.problem
      ? { ...row, problem: "Another file ships under the same name." }
      : row
  );
}

/** The naming payload for the build and the delivery, or null if a row is wrong. */
export function namingFor(rows) {
  if (!rows.length || rows.some((row) => row.problem)) return null;
  return { perFile: _.fromPairs(rows.map((row) => [row.sip.fileName, row.overrides])) };
}

/** What a failed job says, including a refusal the container explained. */
function failureMessage(response, fallback) {
  const events = _.get(response, "error.events") || [];
  const refusal = events.find((event) => event.type === "error" && event.message);
  if (refusal) return refusal.message;
  return describeJobFailureWithReasons(response, { fallback, repeatable: false });
}

export default function AdsManagerTaskDialog({
  editor,
  open,
  folder,
  memory,
  onPickFolder,
  onClose,
  context,
  isRefreshing,
  refreshError,
  onRefresh,
}) {
  const classes = useStyles();
  const memoryKey = `adsmanager:${folder}`;
  const saved = useRef(null);
  if (!saved.current) saved.current = memory.current[memoryKey] || {};

  const [preview, setPreview] = useState(saved.current.preview || null);
  const [delivery, setDelivery] = useState(saved.current.delivery || null);
  const [subsetFonts, setSubsetFonts] = useState(saved.current.subsetFonts !== false);
  const [choices, setChoices] = useState(saved.current.choices || {});
  // Whether the person has looked at what the model filled. Reset whenever it fills more.
  const [aiConfirmed, setAiConfirmed] = useState(saved.current.aiConfirmed === true);
  const [isFilling, setIsFilling] = useState(false);
  const [fillNote, setFillNote] = useState(null);
  // `fileName|template` pairs already put to the model: it is asked once per
  // choice, so typing over (or clearing) a value never triggers another guess.
  const asked = useRef(new Set(saved.current.asked || []));
  const [error, setError] = useState(saved.current.error || null);
  const [isResendOpen, setIsResendOpen] = useState(false);

  useEffect(() => {
    memory.current[memoryKey] = {
      preview, delivery, subsetFonts, choices, error, aiConfirmed, asked: [...asked.current],
    };
  }, [memory, memoryKey, preview, delivery, subsetFonts, choices, error, aiConfirmed]);

  const folders = useMemo(() => collectFolderPaths(editor.snapshots), [editor.snapshots]);
  const sips = useMemo(() => folderSips(editor.snapshots, folder), [editor.snapshots, folder]);
  const names = context.names || [];
  const rows = useMemo(() => deliveryRows(sips, names, choices), [sips, names, choices]);
  const naming = useMemo(() => namingFor(rows), [rows]);

  const buildJob = editor.jobs.get("previewTask", folder);
  const deliverJob = editor.jobs.get("deliverTask", folder);
  const isPreviewing = _.get(buildJob, "state") === "running";
  const isDelivering = _.get(deliverJob, "state") === "running";
  const runningJob = isPreviewing ? buildJob : isDelivering ? deliverJob : null;
  const progress = editor.jobs.progressOf(runningJob);
  const connectionDown = _.get(runningJob, "connectionDown") || null;
  const unresolvedDelivery = editor.jobs.unresolvedDeliveries[folder] || null;
  const isBusy = isPreviewing || isDelivering;

  const links = _.get(preview, "links", []);
  const freshLinks = links.filter((link) => !link.stale);
  const linkFor = (fileName) => links.find((link) => link.snapshot === fileName) || null;

  // The build has to show what Submit would send: every file built, under the
  // name chosen now. A changed name means building again.
  const builtAsChosen =
    links.length > 0 &&
    rows.every((row) => {
      const link = linkFor(row.sip.fileName);
      return link && !link.stale && row.name && link.file === `${row.name}.html`;
    });
  const optionsChanged = preview && preview.subsetFonts !== subsetFonts;

  const lastDelivery = _.get(preview, "lastDelivery") || null;
  const wasDelivered = Boolean(_.get(lastDelivery, "completedAt"));

  const blocker = context.blocker || null;
  const countMismatch = names.length > 0 && names.length !== sips.length;
  // The model's values are suggestions: nothing builds until a person has seen them.
  const aiFilledCount = _.sum(rows.map((row) => row.aiTags.length));
  const needsAiConfirm = aiFilledCount > 0 && !aiConfirmed;
  const canBuild = !isBusy && sips.length > 0 && Boolean(naming) && !needsAiConfirm;
  const canSubmit =
    !isBusy && Boolean(naming) && !needsAiConfirm && builtAsChosen && !optionsChanged && !blocker && !countMismatch;

  function setChoice(fileName, patch) {
    setChoices((current) => ({ ...current, [fileName]: { ...current[fileName], ...patch } }));
  }

  // Typing a value makes it the person's own: it stops being "filled by AI".
  function setTagValue(fileName, tag, value) {
    setChoices((current) => {
      const choice = current[fileName] || {};
      return {
        ...current,
        [fileName]: {
          ...choice,
          tags: { ...choice.tags, [tag]: value },
          ai: (choice.ai || []).filter((entry) => entry !== tag),
        },
      };
    });
  }

  /**
   * Ask the container's model for the tags no one has filled yet. Only the
   * empty ones are touched, and a value that arrives is marked as the model's.
   */
  async function suggestTags({ retry = false } = {}) {
    const wanted = rows.filter((row) => {
      if (!row.tags.length || !row.tags.some((tag) => !String(row.values[tag] || "").trim())) return false;
      const key = `${row.sip.fileName}|${row.template}`;
      if (retry) asked.current.delete(key);
      return !asked.current.has(key);
    });
    if (!wanted.length) return;
    for (const row of wanted) asked.current.add(`${row.sip.fileName}|${row.template}`);

    setIsFilling(true);
    setFillNote(null);
    try {
      const response = await asanaAdsManagerFillTags(
        editor.baseID,
        folder,
        wanted.map((row) => ({
          id: row.sip.fileName,
          template: row.template,
          fileName: row.sip.fileName,
          exportName: row.sip.exportName || null,
        }))
      );
      const data = response && response.data;
      if (!response || !response.ok || !data) {
        setFillNote("Could not suggest values for the tags — type them in.");
        return;
      }
      let arrived = false;
      setChoices((current) => {
        const next = { ...current };
        for (const [fileName, values] of Object.entries(data.filled || {})) {
          const choice = next[fileName] || {};
          const tags = { ...choice.tags };
          const ai = [...(choice.ai || [])];
          for (const [tag, value] of Object.entries(values || {})) {
            if (String(tags[tag] || "").trim() || !cleanTagValue(value)) continue;
            tags[tag] = cleanTagValue(value);
            if (!ai.includes(tag)) ai.push(tag);
            arrived = true;
          }
          next[fileName] = { ...choice, tags, ai };
        }
        return next;
      });
      if (arrived) setAiConfirmed(false);
      const left = _.sum(Object.values(data.unresolved || {}).map((list) => list.length));
      if (data.error) setFillNote("The suggestion service is unavailable — type the tags in.");
      else if (left) setFillNote("Some tags could not be worked out from the ticket — type them in.");
    } catch (err) {
      setFillNote("Could not suggest values for the tags — type them in.");
    } finally {
      setIsFilling(false);
    }
  }

  // Suggest once per file and listed name, as soon as it has tags to fill.
  useEffect(() => {
    if (!open || isFilling || !rows.some((row) => row.tags.length)) return;
    suggestTags();
  }, [open, rows, isFilling]);

  function runPreview() {
    if (!naming) return;
    setError(null);
    setDelivery(null);
    const options = { subsetFonts, naming };
    editor.jobs.start({
      type: "previewTask",
      scope: folder,
      run: async (hooks) => {
        // Whatever the editor still has queued must reach disk first, or the
        // build would show the version on disk rather than the one on screen.
        await editor.flushSave();
        return previewTask(editor.baseID, folder, { ...options, ...hooks });
      },
    });
  }

  useEffect(() => {
    if (!open || !buildJob || buildJob.state !== "finished") return;
    const { response } = buildJob;
    editor.jobs.consume(buildJob.key);
    if (!response.ok) {
      setError(describeJobFailureWithReasons(response, { fallback: "Build failed.", repeatable: true }));
      return;
    }
    setError(null);
    setPreview(response.data);
  }, [open, buildJob]);

  function runDeliver(dryRun = false) {
    setError(null);
    editor.jobs.start({
      meta: { dryRun, wasDelivered },
      type: "deliverTask",
      scope: folder,
      run: (hooks) =>
        deliverTask(editor.baseID, folder, {
          naming,
          workflow: "adsmanager",
          ...(dryRun ? { dryRun: true } : {}),
          ...hooks,
        }),
    });
  }

  useEffect(() => {
    if (!open || !deliverJob || deliverJob.state !== "finished") return;
    const { response, meta } = deliverJob;
    const dryRun = Boolean(_.get(meta, "dryRun") || _.get(response, "data.dryRun"));
    editor.jobs.consume(deliverJob.key);

    if (!response.ok) {
      const code = jobFailureCode(response);
      if (code === "PREVIEW_REQUIRED") {
        setError("Build this task first: submitting ships the files the build produced.");
      } else if (code === "BUILD_OUTDATED") {
        setError("The folder changed since this build. Build again — Submit ships the built files.");
      } else if (code === "ASANA_TASK_NOT_BOUND") {
        setError("This folder is not bound to its Asana task any more. Open it again to check.");
      } else {
        setError(failureMessage(response, "Submit failed."));
      }
      return;
    }

    setDelivery(response.data);
    if (!dryRun) {
      setPreview((current) =>
        current
          ? {
            ...current,
            lastDelivery: {
              completedAt: _.get(response, "data.delivery.completedAt") || null,
              by: _.get(response, "data.delivery.humanApproved.by", null),
              steps: _.pick(_.get(response, "data.delivery") || {}, ["plec", "drive", "asana"]),
              driveLink: _.get(response, "data.driveLink") || null,
              previewUrl: _.get(response, "data.previewUrl") || null,
            },
          }
          : current
      );
      // The subtask's link, its assignee and the task's section all just changed.
      onRefresh();
    }
  }, [open, deliverJob]);

  function submitDelivery() {
    if (unresolvedDelivery) {
      setIsResendOpen(true);
      return;
    }
    runDeliver(false);
  }

  function openPreviews() {
    for (const link of freshLinks) window.open(link.url, "_blank", "noreferrer");
  }

  const subtask = context.currentSubtask;
  const handoff = _.get(delivery, "steps.asana") || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { backgroundColor: PANEL_BG } }}>
      <DialogTitle>Build &amp; submit — ACS Ads Manager</DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          size="small"
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          label="Folder"
          value={folder}
          onChange={(event) => onPickFolder(event.target.value)}
        >
          <option value="">Select a folder…</option>
          {folders.map((path) => (
            <option key={path} value={path}>
              {path}
            </option>
          ))}
        </TextField>

        {/* ── The ticket ─────────────────────────────────────────────────── */}
        <div className={classes.section}>
          <div className={classes.cardRaised}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <div className={classes.summaryName}>
                {context.task.name}{" "}
                {context.task.url && (
                  <a href={context.task.url} target="_blank" rel="noreferrer" style={{ fontSize: "12px" }}>
                    open in Asana
                  </a>
                )}
              </div>
              <Button
                size="small"
                className={classes.iconButton}
                disabled={isRefreshing}
                onClick={onRefresh}
                title="Read the ticket again from Asana"
              >
                {isRefreshing ? <CircularProgress size={14} /> : <RefreshIcon style={{ fontSize: "16px" }} />}
              </Button>
            </div>
            <div className={classes.chips}>
              <span className={classes.chip}>{context.task.section || "no section"}</span>
              <span className={classes.chip}>
                {sips.length} file{sips.length === 1 ? "" : "s"} · {names.length} name{names.length === 1 ? "" : "s"} in ticket
              </span>
              {preview && (
                <span className={`${classes.chip} ${builtAsChosen ? classes.chipOk : classes.chipWarn}`}>
                  {builtAsChosen ? "built as named" : "build again"}
                </span>
              )}
            </div>

            <div style={{ marginTop: "10px" }}>
              <div className={classes.fact}>
                <span className={classes.factName}>ACS Owner</span>
                <span className={classes.factValue}>{_.get(context, "acsOwner.name") || "—"}</span>
              </div>
              <div className={classes.fact}>
                <span className={classes.factName}>Drive folder</span>
                <span className={classes.factValue}>
                  {context.driveFolderUrl ? (
                    <a href={context.driveFolderUrl} target="_blank" rel="noreferrer">
                      {context.driveFolderUrl}
                    </a>
                  ) : (
                    <span style={{ color: "#e0b96a" }}>
                      not there yet — it appears about 5 minutes after the claim
                    </span>
                  )}
                </span>
              </div>
              <div className={classes.fact}>
                <span className={classes.factName}>Current round</span>
                <span className={classes.factValue}>
                  {subtask ? (
                    <>
                      {subtask.url ? (
                        <a href={subtask.url} target="_blank" rel="noreferrer">
                          {subtask.name}
                        </a>
                      ) : (
                        subtask.name
                      )}
                      {subtask.previewLink ? " · has a preview link" : ""}
                    </>
                  ) : (
                    <span style={{ color: "#ef9a9a" }}>no open subtask</span>
                  )}
                </span>
              </div>
            </div>

            {blocker && <Typography className={classes.warning}>Cannot submit yet: {blocker.message}.</Typography>}
            {refreshError && <Typography className={classes.failure}>{refreshError}</Typography>}
          </div>
        </div>

        {/* ── Files and the names they ship under ─────────────────────────── */}
        <div className={classes.section}>
          <div className={classes.sectionHead}>
            <span className={classes.sectionTitle}>Files and their ticket names</span>
            <TaskReviewButton
              baseID={editor.baseID}
              title={preview?.taskName || context.task.name || folder}
              entries={rows.map((row) => ({
                url: linkFor(row.sip.fileName)?.url,
                label: row.sip.fileName,
              })).filter((entry) => entry.url)}
              disabled={isBusy}
            />
          </div>
          <div className={classes.card}>
            {sips.length === 0 && <div className={classes.note}>This folder has no snapshots.</div>}
            {rows.map((row, index) => {
              const link = linkFor(row.sip.fileName);
              return (
                <div key={row.sip.fileName} className={`${classes.fileEntry} ${index ? classes.fileRowDivided : ""}`}>
                  <div className={classes.fileHead}>
                    <span style={{ flex: 1 }}>{row.sip.fileName}</span>
                    {link && formatBytes(link.sizeBytes) && (
                      <span className={classes.fileSize}>{formatBytes(link.sizeBytes)}</span>
                    )}
                    {link && (
                      <a className={classes.fileLink} href={link.url} target="_blank" rel="noreferrer" title="Open this build">
                        <LaunchIcon style={{ fontSize: "14px" }} />
                      </a>
                    )}
                  </div>
                  <div className={classes.fileName}>
                    {row.template ? (
                      <NameParts template={row.template} values={row.values} classes={classes} />
                    ) : (
                      <span style={{ opacity: 0.6 }}>no name chosen</span>
                    )}
                  </div>
                  <div className={classes.fileControls}>
                    {names.length > 1 && (
                      <TextField
                        select
                        size="small"
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        label="Ticket name"
                        style={{ flex: "1 1 320px" }}
                        value={row.template}
                        disabled={isBusy}
                        onChange={(event) => setChoice(row.sip.fileName, { template: event.target.value })}
                      >
                        <option value="">Choose…</option>
                        {names.map((name, nameIndex) => (
                          <option key={name} value={name}>
                            {nameIndex + 1}. {name}
                          </option>
                        ))}
                      </TextField>
                    )}
                  </div>
                  {row.tags.length > 0 && (
                    <div className={classes.tagFields}>
                      {row.tags.map((tag) => (
                        <TextField
                          key={tag}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          label={row.aiTags.includes(tag) ? `${tag} · filled by AI` : tag}
                          style={{ flex: "1 1 180px" }}
                          value={row.values[tag] || ""}
                          disabled={isBusy}
                          error={Boolean(row.values[tag]) && !cleanTagValue(row.values[tag])}
                          onChange={(event) => setTagValue(row.sip.fileName, tag, event.target.value)}
                        />
                      ))}
                    </div>
                  )}
                  {row.problem && <Typography className={classes.failure}>{row.problem}</Typography>}
                </div>
              );
            })}
          </div>
          {names.length === 0 && (
            <Typography className={classes.warning}>
              The ticket description lists no file names, so nothing can be built under the
              ticket&apos;s names. Check the description in Asana.
            </Typography>
          )}
          {countMismatch && (
            <Typography className={classes.warning}>
              The ticket lists {names.length} name{names.length === 1 ? "" : "s"} but this folder has{" "}
              {sips.length} file{sips.length === 1 ? "" : "s"} — every listed asset has to ship, and
              nothing else.
            </Typography>
          )}
          {(isFilling || aiFilledCount > 0 || fillNote || rows.some((row) => row.tags.length)) && (
            <div style={{ marginTop: "8px" }}>
              {isFilling && (
                <div className={classes.busy}>
                  <CircularProgress size={14} />
                  <span className={classes.waitLine}>Reading the ticket to fill the tags…</span>
                </div>
              )}
              {fillNote && <Typography className={classes.warning}>{fillNote}</Typography>}
              {aiFilledCount > 0 && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      color="primary"
                      checked={aiConfirmed}
                      disabled={isBusy}
                      onChange={(event) => setAiConfirmed(event.target.checked)}
                    />
                  }
                  label={
                    <span style={{ fontSize: "13px" }}>
                      I checked the {aiFilledCount} tag{aiFilledCount === 1 ? "" : "s"} the AI filled in
                    </span>
                  }
                />
              )}
              {rows.some((row) => row.tags.length) && (
                <Button size="small" disabled={isBusy || isFilling} onClick={() => suggestTags({ retry: true })}>
                  Fill empty tags with AI
                </Button>
              )}
            </div>
          )}
          <div className={classes.note}>
            The highlighted parts are not the ticket&apos;s: <code>{VENDOR_TAG}</code> for the vendor and{" "}
            <code>{AI_USAGE}</code> for the AI usage are always ours; any other tag (<code>&lt;concept1&gt;</code>,
            …) is worked out from the words the ticket writes under the name, and can be typed over. Everything
            else comes from the ticket, and the build stages each file under exactly this name.
          </div>
        </div>

        {/* ── Build options ─────────────────────────────────────────────────── */}
        <div className={classes.section}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={subsetFonts}
                disabled={isBusy}
                onChange={(event) => setSubsetFonts(event.target.checked)}
              />
            }
            label={<span style={{ fontSize: "13px" }}>Shrink fonts to used characters</span>}
          />
          {preview && !builtAsChosen && (
            <Typography className={classes.warning}>
              The names changed since the last build — build again before submitting.
            </Typography>
          )}
          {optionsChanged && (
            <Typography className={classes.warning}>A build option changed since the last build — build again.</Typography>
          )}
        </div>

        {lastDelivery && (
          <div className={classes.section}>
            <div className={classes.card}>
              {wasDelivered ? (
                <Typography className={classes.ok} style={{ marginTop: 0 }}>
                  Last handed off {lastDelivery.by ? `by ${lastDelivery.by} ` : ""}on{" "}
                  {formatStamp(lastDelivery.completedAt)}.
                </Typography>
              ) : (
                <Typography className={classes.warning} style={{ marginTop: 0 }}>
                  An earlier submit did not finish — check the subtask before sending again.
                </Typography>
              )}
              <div className={classes.note}>
                Submitting again overwrites the file in ACS&apos;s Drive folder, makes a fresh preview link
                and writes it on the open subtask{subtask ? ` (${subtask.name})` : ""}.
              </div>
            </div>
          </div>
        )}

        {/* ── What happened ───────────────────────────────────────────────── */}
        {isPreviewing && (
          <div className={classes.busy}>
            <CircularProgress size={18} />
            <div>
              <Typography style={{ fontSize: "13px" }}>
                {progress || "Building in the agent container — a couple of minutes per SIP."}
              </Typography>
              <BuildWaitLine events={_.get(buildJob, "events")} active={isPreviewing} className={classes.waitLine} />
            </div>
          </div>
        )}
        {isDelivering && (
          <div className={classes.busy}>
            <CircularProgress size={18} />
            <Typography style={{ fontSize: "13px" }}>
              {progress || "Uploading to the preview tool and Drive, then handing off on Asana…"}
            </Typography>
          </div>
        )}
        {isBusy && connectionDown && (
          <Typography className={classes.warning}>
            Lost contact with the server {Math.round((connectionDown.sinceMs || 0) / 1000)}s ago — still
            retrying. The job keeps running there, whether or not this stays open.
          </Typography>
        )}

        {preview && (
          <div className={classes.section}>
            {(preview.failed || []).map((item, index) => (
              <Typography className={classes.failure} key={`${item.snapshot}-${index}`}>
                {item.snapshot}: {item.error}
              </Typography>
            ))}
            {(preview.warnings || []).map((message) => (
              <Typography className={classes.warning} key={message}>
                {message}
              </Typography>
            ))}
          </div>
        )}

        {delivery && (
          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>
                {delivery.dryRun ? "Test — nothing was sent" : delivery.ok === false ? "Stopped partway" : "Delivered"}
              </span>
            </div>
            <div className={classes.card}>
              {Object.entries(delivery.steps || {}).map(([step, result]) => (
                <React.Fragment key={step}>
                  <div className={classes.step}>
                    <span className={classes.stepName}>{STEP_LABELS[step] || step}</span>
                    {result.error ? (
                      <span style={{ color: "#ef9a9a" }}>{result.message || result.error}</span>
                    ) : (
                      <span style={{ color: "#a5d6a7" }}>{delivery.dryRun ? "ready" : "done"}</span>
                    )}
                  </div>
                  {step === "drive" && !result.error && (result.driveLink || result.trackingFolderUrl) && (
                    <div className={classes.driveTargets}>
                      {[
                        { label: "Asana task folder", url: result.driveLink },
                        {
                          label: `Pending${result.trackingFolderPath ? ` / ${result.trackingFolderPath}` : ""}`,
                          url: result.trackingFolderUrl,
                          parent: Boolean(result.trackingFolderPath),
                        },
                      ].filter((target) => target.url).map((target) => (
                        <div className={classes.step} key={target.label}>
                          <span className={classes.stepName}>{target.label}</span>
                          <a className={classes.driveTargetLink} href={target.url} target="_blank" rel="noreferrer" title={target.url}>
                            <span>{target.url.replace(/^https:\/\//, "")}{target.parent ? " (parent folder)" : ""}</span>
                            <LaunchIcon style={{ fontSize: "14px", flexShrink: 0 }} />
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
              {delivery.previewUrl && (
                <div className={classes.step} style={{ marginTop: "6px" }}>
                  <span className={classes.stepName}>Preview link</span>
                  <a href={delivery.previewUrl} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all" }}>
                    {delivery.previewUrl}
                  </a>
                </div>
              )}
            </div>

            {handoff && handoff.plan && (
              <div className={classes.card} style={{ marginTop: "10px" }}>
                <div style={{ fontSize: "12px", color: FIELD_LABEL, marginBottom: "6px" }}>
                  {delivery.dryRun ? "Submit would:" : "On Asana:"}
                </div>
                {handoff.plan.map((item) => {
                  const done = (handoff.done || []).includes(item.action);
                  return (
                    <div className={classes.step} key={item.action}>
                      <span style={{ width: "16px", color: done ? "#a5d6a7" : FIELD_LABEL }}>
                        {done ? "✓" : "•"}
                      </span>
                      <span className={classes.stepName}>
                        {HANDOFF_LABELS[item.action] || item.action}
                        {["preview_link", "preview_comment"].includes(item.action) ? ` ${item.target}` : ""}
                      </span>
                      <span style={{ wordBreak: "break-all" }}>
                        {["preview_link", "preview_comment"].includes(item.action) ? "the link above" : `to ${item.value}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {(delivery.warnings || []).map((message) => (
              <Typography className={classes.warning} key={message}>
                {message}
              </Typography>
            ))}
          </div>
        )}

        {unresolvedDelivery && !isBusy && (
          <Typography className={classes.warning}>
            A submit for this folder ended without an answer. Check the subtask and the Drive folder
            before sending it again.
          </Typography>
        )}

        {error && <Typography className={classes.failure}>{error}</Typography>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button disabled={!canBuild} onClick={runPreview}>
          Build
        </Button>
        <Button disabled={isBusy || freshLinks.length === 0} onClick={openPreviews}>
          Preview All
        </Button>
        {/* Reads the ticket, composes the preview link and the hand-off, runs
            Drive in --dry-run — and writes nothing anywhere. */}
        <Button disabled={!canSubmit} onClick={() => runDeliver(true)}>
          Test
        </Button>
        <Button color="primary" variant="contained" disabled={!canSubmit} onClick={submitDelivery}>
          {wasDelivered ? "Re-deliver" : "Submit"}
        </Button>
      </DialogActions>

      <Dialog open={isResendOpen} onClose={() => setIsResendOpen(false)} maxWidth="xs">
        <DialogTitle>Send this task again?</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: "13px", lineHeight: 1.6 }}>
            The last submit lost contact with the server before it could report back. It runs in the
            agent container, so it has most likely finished. Check the subtask&apos;s Preview Link and
            the Drive folder first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResendOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={() => {
              setIsResendOpen(false);
              editor.jobs.dismissUnresolvedDelivery(folder);
              runDeliver(false);
            }}
          >
            Send again
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
